/**
 * UtilityPanel — multi-mode right-side panel.
 *
 * Mirrors the prototype's `#utilityPanel` (shell.js / shell.css). One panel
 * surface, four primary modes selected via `data-util-mode`:
 *   - context       Active Context (Assets / Agents / Prompts / Knowledge)
 *   - reasoning     Reasoning trace (per card/workspace)
 *   - activity      User Edit Log
 *   - notifications Bell-icon popup (chromeless, full-height)
 *
 * Two secondary modes that share the same chrome but render different bodies:
 *   - ws-chats       Workspace-scoped chat history (per pageId)
 *   - project-chats  Engagement-scoped chat history (per engId)
 *
 * The panel is registered with `usePanelRegistry` so opening it deactivates
 * other mutually-exclusive panels (notifications popup, prompt improver).
 * Reasoning, AI Panel, and Workspace Chats are NOT mutually exclusive and
 * may coexist (matches the Session 60 panel decoupling).
 *
 * Resize is wired via the existing `usePanel` hook — drag from the left
 * edge. Min 320 / Max 720 by default. Width persists in component state for
 * the session; we'll lift to a store + localStorage in a later phase.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { UtilityPanelMode } from '../../types';
import { ContextPanelBody } from './bodies/ContextPanelBody';
import type { ContextTab } from './bodies/ContextPanelBody';
import { ActivityPanelBody } from './bodies/ActivityPanelBody';
import { ChatHistoryPanelBody } from './bodies/ChatHistoryPanelBody';

interface UtilityPanelProps {
  mode: UtilityPanelMode;
  onClose: () => void;
  /** Page id used by Workspace Chats / Project Chats to scope its list. */
  scopeId?: string | null;
  contextTabOverride?: ContextTab | null;
}

const PANEL_MIN_WIDTH = 320;
const PANEL_MAX_WIDTH = 720;
const PANEL_DEFAULT_WIDTH = 400;

const MODE_TITLES: Record<Exclude<UtilityPanelMode, null>, string> = {
  context: 'Context',
  reasoning: 'Reasoning',
  activity: 'User Edit Log',
  notifications: 'Notifications',
  'ws-chats': 'Workspace Chats',
  'project-chats': 'Project Chats',
  'prompt-improver': 'Prompt Improver',
};

export const UtilityPanel = React.memo(function UtilityPanel({
  mode,
  onClose,
  scopeId,
  contextTabOverride = null,
}: UtilityPanelProps) {
  const [width, setWidth] = useState<number>(PANEL_DEFAULT_WIDTH);
  const dragStartRef = useRef<{ x: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStartRef.current = { x: e.clientX, startWidth: width };

      const onMove = (ev: MouseEvent) => {
        const drag = dragStartRef.current;
        if (!drag) return;
        // Right-edge panel — dragging LEFT increases width.
        const dx = drag.x - ev.clientX;
        const next = Math.max(
          PANEL_MIN_WIDTH,
          Math.min(PANEL_MAX_WIDTH, drag.startWidth + dx)
        );
        setWidth(next);
      };

      const onUp = () => {
        dragStartRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [width]
  );

  const body = useMemo(() => {
    if (mode === null) return null;
    switch (mode) {
      case 'context':
        return <ContextPanelBody activeTabOverride={contextTabOverride} />;      
      case 'activity':
        return <ActivityPanelBody />;
      case 'ws-chats':
      case 'project-chats':
        return <ChatHistoryPanelBody scope={mode === 'ws-chats' ? 'workspace' : 'project'} scopeId={scopeId ?? null} />;
      // Notifications + Prompt Improver render in their own components.
      default:
        return null;
    }
  }, [contextTabOverride, mode, scopeId]);

  if (mode === null) return null;

  const title = MODE_TITLES[mode];

  return (
    <aside
      className="utility-panel utility-panel--open"
      data-util-mode={mode}
      style={{ width: `${width}px` }}
      role="complementary"
      aria-label={title}
    >
      <div
        className="utility-panel__resize-handle"
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
      />
      <div className="utility-panel__card">
        <header className="utility-panel__header">
          <h2 className="utility-panel__title">{title}</h2>
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            aria-label="Close panel"
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>
        <div className="utility-panel__body">{body}</div>
      </div>
    </aside>
  );
});

export default UtilityPanel;
